import splitArrayRole from "../../../Preview/Block/BlockType/BlockParts/utils/splitArrayRole";
import { splitGutter } from "../../../Preview/Block/BlockType/BlockParts/gutterUtils";
import {
  composeBaselineRow,
  emitLine,
  layoutParagraph,
  prepareText,
  resolveFont,
  resolveLineHeightPx,
  wrapText,
} from "../text";

// Default size: RoleNameBlock2 has no text-[18px] wrapper, so it inherits
// the 16px root size.
const ROLE_NAME_DEFAULT_SIZE = 16;
const RAGGED_OUTER_PADDING = 10;

const roleText = (item) => (item?.role == null ? "" : String(item.role).trim());
const nameText = (item) => (item?.name == null ? "" : String(item.name).trim());

export const isRoleRow = (item) => {
  const role = roleText(item).toLowerCase();
  if (!role) return false;
  return !(role === "no roll" || role === "." || role === ". .");
};

export const isNameRow = (item) => Boolean(nameText(item));

export const groupByRole = (columnItems = []) => {
  const groups = [];
  columnItems.forEach((item) => {
    if (isRoleRow(item) || groups.length === 0) groups.push([item]);
    else groups[groups.length - 1].push(item);
  });
  return groups;
};

// RoleName2.alignWrappedLines: a wrapped role keeps its first line at the
// top of the pair and a wrapped name keeps its last line at the bottom.
export function alignWrappedLines(leftLines, rightLines, leftIsRole = true) {
  const left = leftLines?.length ? leftLines : [""];
  const right = rightLines?.length ? rightLines : [""];
  if (left.length <= 1 && right.length <= 1) return null;
  const count = Math.max(left.length, right.length);
  const pad = (lines, isRole) => {
    const blanks = Array(count - lines.length).fill("");
    return isRole ? [...lines, ...blanks] : [...blanks, ...lines];
  };
  return { left: pad(left, leftIsRole), right: pad(right, !leftIsRole) };
}

const getSplitTextAlign = (alignment) => {
  switch (alignment) {
    case "left":
      return ["left", "left"];
    case "right":
      return ["right", "right"];
    case "justify":
      return ["left", "right"];
    default:
      return ["right", "left"];
  }
};

const minContentWidth = (measurer, font, text) =>
  wrapText(measurer, font, text, 0.001).reduce(
    (max, line) => Math.max(max, measurer.width(font, line)),
    0,
  );

const safeSplitOptions = (options) =>
  Number(options?.columns) >= 1 ? options : { ...options, columns: 1 };

/**
 * Two baseline-aligned cells (table row or grid row pair). Wrapped text is
 * split into explicit rows exactly like RoleNamePair.
 */
function layoutPairRows(ops, measurer, { top, leftIsRole, left, right }) {
  const leftText = prepareText(left.text, left.font);
  const rightText = prepareText(right.text, right.font);
  const leftLines = wrapText(measurer, left.font, leftText, left.width);
  const rightLines = wrapText(measurer, right.font, rightText, right.width);
  const aligned = alignWrappedLines(leftLines, rightLines, leftIsRole);
  const rows = aligned
    ? aligned.left.map((line, i) => [line, aligned.right[i]])
    : [[leftText, rightText]];

  let y = top;
  rows.forEach(([leftLine, rightLine]) => {
    const row = composeBaselineRow(measurer, [
      { font: left.font, lineHeightPx: left.lineHeightPx },
      { font: right.font, lineHeightPx: right.lineHeightPx },
    ]);
    [
      [left, leftLine],
      [right, rightLine],
    ].forEach(([cell, line]) => {
      if (!line || cell.hidden) return;
      emitLine(ops, {
        measurer,
        font: cell.font,
        text: line,
        left: cell.x,
        width: cell.width,
        baseline: y + row.baseline,
        align: cell.align,
        color: cell.color,
        decoration: cell.decoration,
        lineTop: y,
        lineBottom: y + row.height,
      });
    });
    y += row.height;
  });
  return y - top;
}

export function layoutRoleName(ctx) {
  const { ops, measurer, options = {}, settings, fontStyle, items, left, width, top } = ctx;
  const roleStyle = fontStyle?.role || {};
  const nameStyle = fontStyle?.name || {};
  const visible = (items || []).filter((item) => isRoleRow(item) || isNameRow(item));
  const roleFont = resolveFont(roleStyle, { size: ROLE_NAME_DEFAULT_SIZE });
  const nameFont = resolveFont(nameStyle, { size: ROLE_NAME_DEFAULT_SIZE });

  const pairGapPx =
    Number(nameStyle.marginBottom ?? roleStyle.marginBottom ?? options.row_gap ?? 0) || 0;

  const containerPct = Number(options.column_width) * Number(options.columns);
  const containerWidth = Number.isFinite(containerPct)
    ? (width * containerPct) / 100
    : width;
  const containerLeft =
    containerWidth > width ? left : left + (width - containerWidth) / 2;

  const split = splitArrayRole(visible, safeSplitOptions(options)).ans;
  const nameOnLeft = Boolean(options.name_on_left);

  // Double-column cells use `Number(lineHeight) || 1`; stacked spans use
  // the raw value (missing → inherited).
  const roleLhPx = roleFont.size * (Number(roleStyle.lineHeight) || 1);
  const nameLhPx = nameFont.size * (Number(nameStyle.lineHeight) || 1);

  const pairCells = (item, { ragged = false } = {}) => {
    const roleMissing = item.role == null;
    const leftText = nameOnLeft
      ? item.name == null
        ? ""
        : String(item.name)
      : roleMissing
      ? ragged
        ? ". "
        : "No Roll"
      : String(item.role);
    const rightText = nameOnLeft
      ? roleMissing
        ? ragged
          ? " ."
          : "No Roll"
        : String(item.role)
      : item.name == null
      ? ""
      : String(item.name);
    const roleCell = {
      font: roleFont,
      lineHeightPx: roleLhPx,
      color: roleStyle.color,
      decoration: roleMissing ? null : roleStyle.textDecoration,
      hidden: roleMissing,
    };
    const nameCell = {
      font: nameFont,
      lineHeightPx: nameLhPx,
      color: nameStyle.color,
      decoration: nameStyle.textDecoration,
      hidden: false,
    };
    return {
      leftIsRole: !nameOnLeft,
      left: { ...(nameOnLeft ? nameCell : roleCell), text: leftText },
      right: { ...(nameOnLeft ? roleCell : nameCell), text: rightText },
    };
  };

  // --- Single column: role stacked over name -------------------------------
  if (!options.double_columns) {
    const stackedAlign =
      options.text_alignment === "left"
        ? "left"
        : options.text_alignment === "right"
        ? "right"
        : "center";
    const colWidth = containerWidth / Math.max(1, split.length);
    const roleStackLh = resolveLineHeightPx(roleFont, roleStyle.lineHeight);
    const nameStackLh = resolveLineHeightPx(nameFont, nameStyle.lineHeight);
    let height = 0;

    split.forEach((columnItems, colIndex) => {
      const colLeft = containerLeft + colIndex * colWidth;
      let y = top;
      groupByRole(columnItems).forEach((group) => {
        const roleItem = group.find(isRoleRow);
        const ordered = nameOnLeft
          ? [
              ...group.filter(isNameRow).map((item) => ({ ...item, role: null })),
              ...(roleItem ? [{ ...roleItem, name: null }] : []),
            ]
          : group;
        ordered.forEach((item) => {
          if (item.role != null) {
            y += layoutParagraph(ops, {
              measurer,
              font: roleFont,
              text: String(item.role),
              left: colLeft + 1,
              width: Math.max(0, colWidth - 2),
              top: y,
              align: stackedAlign,
              lineHeightPx: roleStackLh,
              color: roleStyle.color,
              decoration: roleStyle.textDecoration,
            });
          }
          if (item.name != null) {
            y += layoutParagraph(ops, {
              measurer,
              font: nameFont,
              text: String(item.name),
              left: colLeft + 1,
              width: Math.max(0, colWidth - 2),
              top: y,
              align: stackedAlign,
              lineHeightPx: nameStackLh,
              color: nameStyle.color,
              decoration: nameStyle.textDecoration,
            });
          }
        });
        if (roleItem && pairGapPx > 0) y += pairGapPx;
      });
      height = Math.max(height, y - top);
    });
    return height;
  }

  const splitAlign = getSplitTextAlign(options.text_alignment);

  // --- Ragged: intrinsic tables centered per column ------------------------
  if (options.text_alignment === "ragged") {
    const colWidth = containerWidth / Math.max(1, split.length);
    const raggedGutter = Number(settings?.ragged_gutter);
    const half = Number.isFinite(raggedGutter) ? raggedGutter / 2 : 0;
    let height = 0;

    split.forEach((columnItems, colIndex) => {
      const colLeft = containerLeft + colIndex * colWidth;
      let y = top;
      groupByRole(columnItems).forEach((group) => {
        const roleItem = group.find(isRoleRow);
        group.forEach((item) => {
          if (!isRoleRow(item)) {
            y += layoutParagraph(ops, {
              measurer,
              font: nameFont,
              text: nameText(item),
              left: colLeft,
              width: colWidth,
              top: y,
              align: "center",
              lineHeightPx: nameLhPx,
              color: nameStyle.color,
              decoration: nameStyle.textDecoration,
            });
            return;
          }
          const pair = pairCells(item, { ragged: true });
          const leftPad = RAGGED_OUTER_PADDING + half;
          const rightPad = half + RAGGED_OUTER_PADDING;
          const leftText = prepareText(pair.left.text, pair.left.font);
          const rightText = prepareText(pair.right.text, pair.right.font);
          const max = [
            measurer.width(pair.left.font, leftText) + leftPad,
            measurer.width(pair.right.font, rightText) + rightPad,
          ];
          const min = [
            minContentWidth(measurer, pair.left.font, leftText) + leftPad,
            minContentWidth(measurer, pair.right.font, rightText) + rightPad,
          ];
          const sumMax = max[0] + max[1];
          const sumMin = min[0] + min[1];
          let widths = max;
          if (sumMax > colWidth) {
            widths =
              colWidth <= sumMin
                ? min
                : min.map(
                    (m, i) => m + ((max[i] - m) * (colWidth - sumMin)) / (sumMax - sumMin),
                  );
          }
          const tableWidth = widths[0] + widths[1];
          const tableLeft =
            tableWidth > colWidth ? colLeft : colLeft + (colWidth - tableWidth) / 2;
          y += layoutPairRows(ops, measurer, {
            top: y,
            leftIsRole: pair.leftIsRole,
            left: {
              ...pair.left,
              x: tableLeft + RAGGED_OUTER_PADDING,
              width: Math.max(0, widths[0] - leftPad),
              align: "right",
            },
            right: {
              ...pair.right,
              x: tableLeft + widths[0] + half,
              width: Math.max(0, widths[1] - rightPad),
              align: "left",
            },
          });
        });
        if (roleItem && pairGapPx > 0) y += pairGapPx;
      });
      height = Math.max(height, y - top);
    });
    return height;
  }

  // --- Double column: 50/50 tables, or an optical grid for 2-col gutter ----
  const colCount = Math.max(1, Number(options.columns) || 1);
  const colWidth = containerWidth / colCount;
  const optical = colCount === 2 && options.text_alignment === "gutter";
  const centerColumns = options.columns === 1 || options.text_alignment === "gutter";
  const usedWidth = split.length * colWidth;
  const startLeft = centerColumns
    ? containerLeft + (containerWidth - usedWidth) / 2
    : containerLeft;
  const cellGutter = splitGutter(settings?.gutter);
  const opticalGap = Number(settings?.gutter) || 0;
  let height = 0;

  split.forEach((columnItems, colIndex) => {
    const colLeft = startLeft + colIndex * colWidth;
    let y = top;
    const groups = groupByRole(columnItems);

    if (optical) {
      const pairs = groups.map((group) => group.map((item) => pairCells(item)));
      let maxLeft = 0;
      let maxRight = 0;
      pairs.flat().forEach((pair) => {
        maxLeft = Math.max(
          maxLeft,
          measurer.width(pair.left.font, prepareText(pair.left.text, pair.left.font)),
        );
        maxRight = Math.max(
          maxRight,
          measurer.width(pair.right.font, prepareText(pair.right.text, pair.right.font)),
        );
      });
      const gridWidth = Math.min(colWidth, maxLeft + opticalGap + maxRight);
      const free = Math.max(0, gridWidth - opticalGap);
      let leftTrack = maxLeft;
      let rightTrack = maxRight;
      if (maxLeft + maxRight > free) {
        const share = free / 2;
        if (maxLeft <= share) {
          rightTrack = free - maxLeft;
        } else if (maxRight <= share) {
          leftTrack = free - maxRight;
        } else {
          leftTrack = share;
          rightTrack = share;
        }
      }
      const gridLeft = colLeft + (colWidth - gridWidth) / 2;
      groups.forEach((group, groupIndex) => {
        pairs[groupIndex].forEach((pair) => {
          y += layoutPairRows(ops, measurer, {
            top: y,
            leftIsRole: pair.leftIsRole,
            left: { ...pair.left, x: gridLeft, width: leftTrack, align: splitAlign[0] },
            right: {
              ...pair.right,
              x: gridLeft + leftTrack + opticalGap,
              width: rightTrack,
              align: splitAlign[1],
            },
          });
        });
        if (group.find(isRoleRow) && pairGapPx > 0) y += pairGapPx;
      });
    } else {
      const half = colWidth / 2;
      groups.forEach((group) => {
        group.forEach((item) => {
          const pair = pairCells(item);
          y += layoutPairRows(ops, measurer, {
            top: y,
            leftIsRole: pair.leftIsRole,
            left: {
              ...pair.left,
              x: colLeft,
              width: Math.max(0, half - cellGutter),
              align: splitAlign[0],
            },
            right: {
              ...pair.right,
              x: colLeft + half + cellGutter,
              width: Math.max(0, half - cellGutter),
              align: splitAlign[1],
            },
          });
        });
        if (group.find(isRoleRow) && pairGapPx > 0) y += pairGapPx;
      });
    }
    height = Math.max(height, y - top);
  });
  return height;
}
